import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, MessageSquare, FileText, Layers, Plus, Trash2, X, Clock, Edit, Upload, Image, Calendar } from "lucide-react";
import axios from "axios";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuth() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } };
}

function ImageUpload({ value, onChange, label = "Image" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const token = localStorage.getItem("admin_token");
      const res = await axios.post(`${API}/admin/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      onChange(imageUrl);
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Image upload failed. Max size is 5MB.");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div>
      <label className="block font-body text-sm font-medium text-charcoal mb-1">{label}</label>
      {value && (
        <div className="mb-2 relative inline-block">
          <img src={value} alt="Preview" className="w-32 h-20 object-cover rounded-lg border border-border" />
          <button onClick={() => onChange("")} className="absolute -top-1.5 -right-1.5 bg-white border border-border rounded-full p-0.5 hover:bg-destructive/10">
            <X className="w-3 h-3 text-destructive" />
          </button>
        </div>
      )}
      <div className="flex gap-2">
        <input placeholder="https://... or upload below" value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-4 py-2.5 rounded-xl border border-border bg-champagne font-body text-sm" />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="flex items-center gap-1.5 px-4 py-2.5 bg-navy/10 text-navy rounded-xl font-body text-sm font-medium hover:bg-navy/20 transition-all disabled:opacity-50">
          <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-body text-sm font-medium transition-all ${
        active ? "bg-navy text-white shadow-soft" : "text-charcoal-light hover:bg-champagne-dark"
      }`}
    >
      <Icon className="w-4 h-4" /> <span className="hidden md:inline">{label}</span>
    </button>
  );
}

function ServicesTab() {
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    name: "", slug: "", description: "", short_description: "",
    duration_minutes: 60, price: 0, currency: "SGD", image_url: "", audience: "individual", is_active: true
  });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/services`, getAuth()); setServices(r.data); } catch(e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const resetForm = () => {
    setForm({ name: "", slug: "", description: "", short_description: "", duration_minutes: 60, price: 0, currency: "SGD", image_url: "", audience: "individual", is_active: true });
    setEditingId(null);
    setShowForm(false);
  };

  const save = async () => {
    const payload = { ...form };
    if (!payload.slug) payload.slug = generateSlug(payload.name);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/services/${editingId}`, payload, getAuth());
      } else {
        await axios.post(`${API}/admin/services`, payload, getAuth());
      }
      resetForm();
      load();
    } catch(e) { console.error(e); }
  };

  const edit = (svc) => {
    setForm({
      name: svc.name, slug: svc.slug, description: svc.description,
      short_description: svc.short_description, duration_minutes: svc.duration_minutes,
      price: svc.price || 0, currency: svc.currency || "SGD", image_url: svc.image_url || "",
      audience: svc.audience || "individual", is_active: svc.is_active
    });
    setEditingId(svc.id);
    setShowForm(true);
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/admin/services/${id}`, getAuth()); load(); } catch(e) { console.error(e); }
  };

  return (
    <div data-testid="admin-services-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-semibold text-charcoal">Services</h2>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} data-testid="add-service-btn"
          className="bg-navy text-white px-4 py-2 rounded-xl font-body text-sm font-semibold hover:bg-navy-light transition-all flex items-center gap-1">
          <Plus className="w-4 h-4" /> Add Service
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-border rounded-2xl p-6 mb-6 space-y-4" data-testid="service-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block font-body text-sm font-medium text-charcoal mb-1">Name *</label>
              <input placeholder="Service name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value, slug: generateSlug(e.target.value) })}
                data-testid="service-name-input"
                className="w-full px-4 py-2.5 rounded-xl border border-border bg-champagne font-body text-sm" />
            </div>
            <div>
              <label className="block font-body text-sm font-medium text-charcoal mb-1">Slug</label>
              <input placeholder="auto-generated" value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-border bg-champagne font-body text-sm" />
            </div>
          </div>
          <div>
            <label className="block font-body text-sm font-medium text-charcoal mb-1">Short Description</label>
            <input placeholder="One-line summary" value={form.short_description}
              onChange={(e) => setForm({ ...form, short_description: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl border border-border bg-champagne font-body text-sm" />
          </div>
          <div>
            <label className="block font-body text-sm font-medium text-charcoal mb-1">Full Description *</label>
            <textarea rows={4} placeholder="Detailed description..." value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl border border-border bg-champagne font-body text-sm resize-none" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block font-body text-sm font-medium text-charcoal mb-1">Duration (min)</label>
              <input type="number" value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2.5 rounded-xl border border-border bg-champagne font-body text-sm" />
            </div>
            <div>
              <label className="block font-body text-sm font-medium text-charcoal mb-1">Price (0 = hidden)</label>
              <input type="number" value={form.price}
                onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-2.5 rounded-xl border border-border bg-champagne font-body text-sm" />
            </div>
            <div>
              <label className="block font-body text-sm font-medium text-charcoal mb-1">Currency</label>
              <input value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-border bg-champagne font-body text-sm" />
            </div>
            <div>
              <label className="block font-body text-sm font-medium text-charcoal mb-1">Audience</label>
              <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-border bg-champagne font-body text-sm">
                <option value="individual">Individual</option>
                <option value="family">Family</option>
              </select>
            </div>
          </div>
          <ImageUpload label="Service Image" value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} />
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="rounded" />
            <label className="font-body text-sm text-charcoal">Active (visible on site)</label>
          </div>
          <div className="flex gap-3">
            <button onClick={save} data-testid="save-service-btn"
              className="bg-navy text-white px-5 py-2 rounded-xl font-body text-sm font-semibold hover:bg-navy-light transition-all">
              {editingId ? "Update Service" : "Create Service"}
            </button>
            <button onClick={resetForm}
              className="px-5 py-2 rounded-xl font-body text-sm text-charcoal-light border border-border hover:bg-champagne-dark transition-all">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {services.map((svc) => (
          <div key={svc.id} className="bg-white border border-border rounded-2xl p-5 flex justify-between items-start gap-4" data-testid={`service-item-${svc.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-body font-semibold text-charcoal">{svc.name}</p>
                <span className="px-2 py-0.5 bg-navy/10 text-navy rounded-full font-body text-xs capitalize">{svc.audience}</span>
                {!svc.is_active && <span className="px-2 py-0.5 bg-muted text-charcoal-light rounded-full font-body text-xs">Inactive</span>}
              </div>
              <p className="font-body text-xs text-charcoal-light">
                {svc.duration_minutes} min {svc.price > 0 ? `| ${svc.currency} ${svc.price}` : "| No price set"}
              </p>
              <p className="font-body text-sm text-charcoal-light mt-1 line-clamp-1">{svc.short_description}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => edit(svc)} data-testid={`edit-service-${svc.id}`}
                className="p-1.5 text-charcoal-light hover:text-navy transition-colors"><Edit className="w-4 h-4" /></button>
              <button onClick={() => remove(svc.id)} data-testid={`delete-service-${svc.id}`}
                className="p-1.5 text-charcoal-light hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {services.length === 0 && <p className="font-body text-sm text-charcoal-light">No services yet.</p>}
      </div>
    </div>
  );
}

function TestimonialsTab() {
  const [testimonials, setTestimonials] = useState([]);
  const [form, setForm] = useState({ client_name: "", client_description: "", quote: "", is_featured: true, display_order: 0 });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/testimonials`, getAuth()); setTestimonials(r.data); } catch(e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({ client_name: "", client_description: "", quote: "", is_featured: true, display_order: 0 });
    setEditingId(null);
    setShowForm(false);
  };

  const save = async () => {
    try {
      if (editingId) {
        await axios.put(`${API}/admin/testimonials/${editingId}`, form, getAuth());
      } else {
        await axios.post(`${API}/admin/testimonials`, form, getAuth());
      }
      resetForm();
      load();
    } catch(e) { console.error(e); }
  };

  const edit = (t) => {
    setForm({ client_name: t.client_name, client_description: t.client_description || "", quote: t.quote, is_featured: t.is_featured ?? true, display_order: t.display_order || 0 });
    setEditingId(t.id);
    setShowForm(true);
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/admin/testimonials/${id}`, getAuth()); load(); } catch(e) { console.error(e); }
  };

  return (
    <div data-testid="admin-testimonials-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-semibold text-charcoal">Testimonials</h2>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} data-testid="add-testimonial-btn"
          className="bg-navy text-white px-4 py-2 rounded-xl font-body text-sm font-semibold hover:bg-navy-light transition-all flex items-center gap-1">
          <Plus className="w-4 h-4" /> Add New
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-border rounded-2xl p-5 mb-6 space-y-3" data-testid="testimonial-form">
          <input placeholder="Client name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })}
            data-testid="testimonial-name-input" className="w-full px-4 py-2.5 rounded-xl border border-border bg-champagne font-body text-sm" />
          <input placeholder="Description (e.g. Parent - Child: 5 yo)" value={form.client_description} onChange={(e) => setForm({ ...form, client_description: e.target.value })}
            data-testid="testimonial-desc-input" className="w-full px-4 py-2.5 rounded-xl border border-border bg-champagne font-body text-sm" />
          <textarea placeholder="Their testimonial..." rows={3} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })}
            data-testid="testimonial-quote-input" className="w-full px-4 py-2.5 rounded-xl border border-border bg-champagne font-body text-sm resize-none" />
          <div className="flex gap-3">
            <button onClick={save} data-testid="save-testimonial-btn"
              className="bg-navy text-white px-5 py-2 rounded-xl font-body text-sm font-semibold hover:bg-navy-light transition-all">
              {editingId ? "Update Testimonial" : "Save"}
            </button>
            {editingId && (
              <button onClick={resetForm}
                className="px-5 py-2 rounded-xl font-body text-sm text-charcoal-light border border-border hover:bg-champagne-dark transition-all">
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {testimonials.map((t) => (
          <div key={t.id} className="bg-white border border-border rounded-2xl p-5 flex justify-between items-start gap-4" data-testid={`testimonial-item-${t.id}`}>
            <div>
              <p className="font-body font-semibold text-charcoal text-sm">{t.client_name}</p>
              <p className="font-body text-xs text-charcoal-light">{t.client_description}</p>
              <p className="font-body text-sm text-charcoal-light mt-2 italic">"{t.quote}"</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => edit(t)} data-testid={`edit-testimonial-${t.id}`}
                className="p-1.5 text-charcoal-light hover:text-navy transition-colors"><Edit className="w-4 h-4" /></button>
              <button onClick={() => remove(t.id)} data-testid={`delete-testimonial-${t.id}`}
                className="p-1.5 text-charcoal-light hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({ title: "", slug: "", excerpt: "", content: "", cover_image: "", tags: [], is_published: true, created_at: "" });
  const [tagInput, setTagInput] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const quillModules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["blockquote"],
      ["link"],
      ["clean"],
    ],
  }), []);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/blog`, getAuth()); setPosts(r.data); } catch(e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({ title: "", slug: "", excerpt: "", content: "", cover_image: "", tags: [], is_published: true, created_at: "" });
    setTagInput("");
    setEditingId(null);
    setShowForm(false);
  };

  const generateSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
      setTagInput("");
    }
  };

  const removeTag = (tag) => setForm({ ...form, tags: form.tags.filter((t) => t !== tag) });

  const save = async () => {
    const payload = { ...form };
    if (!payload.slug) payload.slug = generateSlug(payload.title);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/blog/${editingId}`, payload, getAuth());
      } else {
        await axios.post(`${API}/admin/blog`, payload, getAuth());
      }
      resetForm();
      load();
    } catch(e) { console.error(e); }
  };

  const edit = (post) => {
    setForm({ title: post.title, slug: post.slug, excerpt: post.excerpt || "", content: post.content, cover_image: post.cover_image || "", tags: post.tags || [], is_published: post.is_published, created_at: post.created_at || "" });
    setEditingId(post.id);
    setShowForm(true);
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/admin/blog/${id}`, getAuth()); load(); } catch(e) { console.error(e); }
  };

  return (
    <div data-testid="admin-blog-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-semibold text-charcoal">Blog Posts</h2>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} data-testid="add-blog-btn"
          className="bg-navy text-white px-4 py-2 rounded-xl font-body text-sm font-semibold hover:bg-navy-light transition-all flex items-center gap-1">
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-border rounded-2xl p-6 mb-6 space-y-4" data-testid="blog-form">
          <div>
            <label className="block font-body text-sm font-medium text-charcoal mb-1">Title *</label>
            <input placeholder="Post title" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value, slug: generateSlug(e.target.value) })}
              data-testid="blog-title-input"
              className="w-full px-4 py-2.5 rounded-xl border border-border bg-champagne font-body text-sm" />
          </div>
          <div>
            <label className="block font-body text-sm font-medium text-charcoal mb-1">Slug</label>
            <input placeholder="auto-generated-slug" value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              data-testid="blog-slug-input"
              className="w-full px-4 py-2.5 rounded-xl border border-border bg-champagne font-body text-sm" />
          </div>
          <ImageUpload label="Cover Image" value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} />
          <div>
            <label className="block font-body text-sm font-medium text-charcoal mb-1">Excerpt</label>
            <input placeholder="Short summary for cards..." value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              data-testid="blog-excerpt-input"
              className="w-full px-4 py-2.5 rounded-xl border border-border bg-champagne font-body text-sm" />
          </div>
          <div>
            <label className="block font-body text-sm font-medium text-charcoal mb-1">Content *</label>
            <div className="bg-white rounded-xl border border-border overflow-hidden [&_.ql-toolbar]:border-border [&_.ql-container]:border-border [&_.ql-editor]:min-h-[200px] [&_.ql-editor]:font-body [&_.ql-editor]:text-sm">
              <ReactQuill
                theme="snow"
                value={form.content}
                onChange={(val) => setForm({ ...form, content: val })}
                modules={quillModules}
                placeholder="Write your blog post here..."
              />
            </div>
          </div>
          <div>
            <label className="block font-body text-sm font-medium text-charcoal mb-1">Tags</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {form.tags.map((tag) => (
                <span key={tag} className="flex items-center gap-1 px-2 py-1 bg-navy/10 text-navy rounded-full font-body text-xs">
                  {tag} <button onClick={() => removeTag(tag)} className="hover:text-destructive"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input placeholder="Add tag..." value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                data-testid="blog-tag-input"
                className="flex-1 px-3 py-2 rounded-lg border border-border bg-champagne font-body text-sm" />
              <button onClick={addTag} className="px-3 py-2 bg-navy/10 text-navy rounded-lg font-body text-sm font-medium">Add</button>
            </div>
          </div>
          <div>
            <label className="block font-body text-sm font-medium text-charcoal mb-1 flex items-center gap-1.5">
              <Calendar className="w-4 h-4" /> Published Date
            </label>
            <input type="date"
              value={form.created_at ? form.created_at.split("T")[0] : ""}
              onChange={(e) => setForm({ ...form, created_at: e.target.value ? `${e.target.value}T00:00:00+00:00` : "" })}
              data-testid="blog-date-input"
              className="w-full md:w-64 px-4 py-2.5 rounded-xl border border-border bg-champagne font-body text-sm" />
            <p className="font-body text-xs text-charcoal-light mt-1">Leave empty to use today's date</p>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
              data-testid="blog-published-check" className="rounded" />
            <label className="font-body text-sm text-charcoal">Published</label>
          </div>
          <div className="flex gap-3">
            <button onClick={save} data-testid="save-blog-btn"
              className="bg-navy text-white px-5 py-2 rounded-xl font-body text-sm font-semibold hover:bg-navy-light transition-all">
              {editingId ? "Update Post" : "Publish Post"}
            </button>
            <button onClick={resetForm} className="px-5 py-2 rounded-xl font-body text-sm text-charcoal-light border border-border hover:bg-champagne-dark transition-all">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="bg-white border border-border rounded-2xl p-5 flex justify-between items-start gap-4" data-testid={`blog-item-${p.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-body font-semibold text-charcoal">{p.title}</p>
                {!p.is_published && <span className="px-2 py-0.5 bg-muted text-charcoal-light rounded-full font-body text-xs">Draft</span>}
              </div>
              <p className="font-body text-xs text-charcoal-light">{new Date(p.created_at).toLocaleDateString()} | /{p.slug}</p>
              {p.excerpt && <p className="font-body text-sm text-charcoal-light mt-1 line-clamp-1">{p.excerpt}</p>}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => edit(p)} data-testid={`edit-blog-${p.id}`}
                className="p-1.5 text-charcoal-light hover:text-navy transition-colors"><Edit className="w-4 h-4" /></button>
              <button onClick={() => remove(p.id)} data-testid={`delete-blog-${p.id}`}
                className="p-1.5 text-charcoal-light hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {posts.length === 0 && <p className="font-body text-sm text-charcoal-light">No blog posts yet. Create your first post above.</p>}
      </div>
    </div>
  );
}


function SiteImagesTab() {
  const [images, setImages] = useState({ hero_image: "", joyce_photo: "", journal_image: "", about_photo: "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/site-settings`);
      setImages({ hero_image: r.data.hero_image || "", joyce_photo: r.data.joyce_photo || "", journal_image: r.data.journal_image || "", about_photo: r.data.about_photo || "" });
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, images, getAuth());
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const fields = [
    { key: "hero_image", label: "Hero Image (Homepage banner)" },
    { key: "joyce_photo", label: "Joyce's Photo (Homepage — Who I Work With)" },
    { key: "about_photo", label: "Joyce's Photo (About page)" },
    { key: "journal_image", label: "Journal Image (Homepage & Journal page)" },
  ];

  return (
    <div data-testid="admin-site-images-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-semibold text-charcoal">Site Images</h2>
        {saved && <span className="font-body text-sm text-navy font-medium">Saved!</span>}
      </div>
      <div className="bg-white border border-border rounded-2xl p-6 space-y-6">
        {fields.map((f) => (
          <ImageUpload key={f.key} label={f.label} value={images[f.key]} onChange={(url) => setImages({ ...images, [f.key]: url })} />
        ))}
        <button onClick={save} disabled={saving}
          className="bg-navy text-white px-6 py-2.5 rounded-xl font-body text-sm font-semibold hover:bg-navy-light transition-all disabled:opacity-50">
          {saving ? "Saving..." : "Save All Images"}
        </button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("services");
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) navigate("/admin");
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_username");
    navigate("/admin");
  };

  const tabs = [
    { id: "services", icon: Layers, label: "Services" },
    { id: "testimonials", icon: MessageSquare, label: "Testimonials" },
    { id: "blog", icon: FileText, label: "Blog" },
    { id: "site-images", icon: Image, label: "Site Images" },
  ];

  return (
    <div className="min-h-screen bg-champagne" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-border px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <p className="font-heading text-lg font-semibold text-charcoal">Admin Dashboard</p>
            <p className="font-body text-xs text-charcoal-light">Welcome, {localStorage.getItem("admin_username") || "Joyce"}</p>
          </div>
          <button onClick={logout} data-testid="admin-logout-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-xl font-body text-sm text-charcoal-light hover:text-destructive hover:bg-destructive/5 transition-all">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 p-2 bg-champagne-dark/50 rounded-2xl w-fit" data-testid="admin-tabs">
          {tabs.map((t) => (
            <TabButton key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}
              icon={t.icon} label={t.label} testId={`admin-tab-${t.id}`} />
          ))}
        </div>

        {/* Content */}
        {tab === "services" && <ServicesTab />}
        {tab === "testimonials" && <TestimonialsTab />}
        {tab === "blog" && <BlogTab />}
        {tab === "site-images" && <SiteImagesTab />}
      </div>
    </div>
  );
}
